import React, { useState } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { useCountryContext } from '../contexts/CountryContext';
import { useTranslation } from '../hooks/useTranslation';
import { API_CONFIG } from '../config/api';

const CountrySelector: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { selectedCountry, setSelectedCountry, availableCountries } = useCountryContext();
  const { t } = useTranslation();

  const getCountryFlag = (countryCode: string) => {
    const flags: { [key: string]: string } = {
      'ITALIA': '🇮🇹',
      'SVIZZERA': '🇨🇭',
    };
    return flags[countryCode] || '🌍';
  };

  const getCountryName = (countryCode: string) => {
    const lowerCode = API_CONFIG.COUNTRIES[countryCode as keyof typeof API_CONFIG.COUNTRIES]?.code;
    return t(`countries.${lowerCode === 'it' ? 'italia' : 'svizzera'}`);
  };

  const handleCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode as keyof typeof API_CONFIG.COUNTRIES);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center hover:text-pink-200 transition-colors duration-200 bg-white/10 rounded-lg px-3 py-2 backdrop-blur-sm"
      >
        <span className="mr-2">{getCountryFlag(selectedCountry)}</span>
        <span className="hidden sm:inline text-sm font-medium">
          {getCountryName(selectedCountry)}
        </span>
        <ChevronDown 
          className={`ml-2 h-4 w-4 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : 'rotate-0'
          }`}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop per chiudere il menu */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu dropdown */}
          <div className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-lg rounded-lg shadow-xl border border-white/20 z-50">
            <div className="py-2">
              <div className="px-4 py-2 text-sm font-semibold text-gray-800 border-b border-gray-200">
                <div className="flex items-center">
                  <Globe className="h-4 w-4 mr-2 text-gray-600" />
                  {t('countries.selectCountry')}
                </div>
              </div>
              
              {availableCountries.map((countryCode) => (
                <button
                  key={countryCode}
                  onClick={() => handleCountryChange(countryCode)}
                  className={`w-full flex items-center px-4 py-2 text-sm text-left hover:bg-pink-50 transition-colors duration-150 ${
                    selectedCountry === countryCode 
                      ? 'bg-pink-100 text-pink-800 font-semibold' 
                      : 'text-gray-700'
                  }`}
                >
                  <span className="mr-3 text-lg">{getCountryFlag(countryCode)}</span>
                  <span>{getCountryName(countryCode)}</span>
                  {selectedCountry === countryCode && (
                    <span className="ml-auto text-pink-600">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CountrySelector;